/**
 * @author NikolaV
 */
import { Ajax } from '@shared/Ajax';
import Feature, { FeatureLike } from 'ol/Feature';
import { latLonToEN } from '../ol-utils';

import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import { LineString as OlLineString, Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';

import { VehiclesService } from '@shared/vehicles/VehiclesService';
import { VehiclesLayer } from '@openlayers/vehicles/VehiclesLayer';
import { MapOverlays } from '@openlayers/ol-overlays';
import { DateUtils } from '@shared/utils/DateUtils';

const FEATURE_TYPE = 'vehicle';
const FEATURE_TAIL_ID = 'vehicle-tail';

const iconStylesCache = {};

class VehicleTrailDesktopMouseInteraction extends FeatureMouseInteraction<VehiclesLayer> {
    onClick = (feature: Feature, e, n): void => {};

    onMouseOver = (feature: Feature, e, n): void => {
        if (!feature) return;
        const { startTime, endTime, routeStartTime, routeEndTime } = feature.get('data');
        const segmentDuration = endTime ? DateUtils.secondsToDateTime((endTime - startTime) / 1000) : '';
        const routeDuration = routeEndTime ? DateUtils.secondsToDateTime((routeEndTime - routeStartTime) / 1000) : '';

        MapOverlays.showTooltip(
            this.map,
            `
<div class="card padded-half" style='font-size: 12px;'>
<strong>${__('trucks.trip_segment')}:</strong>
 ${DateUtils.formatDateTime(startTime)} - ${endTime ? DateUtils.formatDateTime(endTime) : ''}
 <br>${__('trucks.duration')}: ${segmentDuration}
<br>
<strong style="margin-top: 5px">${__('trucks.trip_route')}:</strong>
 ${DateUtils.formatDateTime(routeStartTime)} - ${routeEndTime ? DateUtils.formatDateTime(routeEndTime) : ''}
 <br>${__('trucks.duration')}: ${routeDuration}
</div>`,
            e,
            n
        );
    };
}

const styles = {
    circle: new olStyle.Style({
        // for debug, draw feature center to offset icon
        image: new olStyle.Circle({
            fill: new olStyle.Fill({
                color: [0, 0, 0, 1],
            }),
            radius: 2,
        }),
    }),

    tail: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: '#359100',
            width: 2,
            lineDash: [10, 4],
        }),
    }),

    tailHover: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: '#359100',
            width: 4,
        }),
    }),
};

function styleFunction(feature: FeatureLike, resolution: number) {
    const vehicle: IVehicle = feature.get('data');
    const isXmas = new Date().getMonth() === 11;
    return createStyle(vehicle, resolution < 6, isXmas);
}

function createStyle(vehicle: IVehicle, showText: boolean = true, xmas = false) {
    const cacheKey = (showText ? vehicle.id : vehicle.type) + '_' + vehicle.reportAlert;
    if (iconStylesCache[cacheKey]) return iconStylesCache[cacheKey];

    try {
        const def = VehiclesService.TYPES[vehicle.type];
        const iconSrc = vehicle.reportAlert ? def.iconGrayscale : def.icon;
        const offsetX = 12;
        const offsetY = 50;

        iconStylesCache[cacheKey] = [
            new olStyle.Style({
                image: new olStyle.Icon({
                    src: iconSrc,
                    anchorXUnits: 'pixels',
                    anchorYUnits: 'pixels',
                    scale: 0.23,
                    anchor: [offsetX, offsetY + 150],
                }),
                text: showText
                    ? new olStyle.Text({
                          text: VehiclesService.VendorNames[vehicle.vendor],
                          offsetY: offsetY - 50,
                          offsetX: offsetX,
                          textAlign: 'left',
                          font: '11px Calibri',
                          fill: new olStyle.Fill({ color: '#ffffff' }),
                          backgroundFill: new olStyle.Fill({ color: 'black' }),
                          padding: [1, 3, 1, 3],
                      })
                    : null,
            }),
            showText
                ? new olStyle.Style({
                      text: new olStyle.Text({
                          text: vehicle.name,
                          offsetY: offsetY - 35,
                          offsetX: -10,
                          textAlign: 'left',
                          font: '11px Calibri',
                          fill: new olStyle.Fill({ color: '#ffffff' }),
                          backgroundFill: new olStyle.Fill({ color: '#808080' }),
                          padding: [1, 3, 1, 3],
                      }),
                  })
                : null,
            styles.circle,
        ].filter(Boolean);

        return iconStylesCache[cacheKey];
    } catch (e) {
        console.error('VehiclesFeatureFactory.createIconStyle()', vehicle);
        console.error('error', e);
        return;
    }
}

function makeFeature(vehicle: IVehicle, mouseInteraction?: FeatureMouseInteraction): Feature {
    if (vehicle.onHold) {
        return null;
    }
    if (!vehicle.position || !vehicle.position.latitude || !vehicle.position.longitude) {
        console.error('VehiclesFeatureFactory.makeFeature() - skipping vehicle, no coordinates: ', vehicle);
        return null;
    }
    const coord: Coordinate = latLonToEN(vehicle.position.latitude, vehicle.position.longitude);
    if (!coord) {
        console.error('VehiclesFeatureFactory.makeFeature() - skipping vehicle, bad coordinates: ', vehicle);
        return null;
    }

    const feature = new Feature({
        geometry: new OlPoint(coord),
    });
    feature.set('data', vehicle);
    feature.set('type', FEATURE_TYPE);
    feature.set('mouseInteraction', mouseInteraction);
    feature.setId(vehicle.id);

    return feature;
}

function makeTailFeatures(routes): Feature[] {
    if (!routes) {
        console.error('VehiclesFeatureFactory.makeTailFeature() - routes undefined ', routes);
        return [];
    }

    if (routes.length === 0) {
        console.log('VehiclesFeatureFactory.makeTailFeature() - routes object empty: ', routes);
        return [];
    }

    const lineFeatures = [];

    routes.forEach((route, routeIndex) => {
        const routeStartTime = route[0]?.timestamp;
        const routeEndTime = route[route.length - 1]?.timestamp;

        // separate feature for each segment since they want them clicakble, also no webgl since it can't have mouse events
        for (let i = 0; i < route.length - 1; i++) {
            const startPos = route[i];
            const endPos = route[i + 1];

            const startCoord = latLonToEN(startPos.latitude, startPos.longitude);
            const endCoord = latLonToEN(endPos.latitude, endPos.longitude);

            const lineFeature = new Feature({
                geometry: new OlLineString([startCoord, endCoord]),
            });

            lineFeature.set('type', FEATURE_TAIL_ID);
            lineFeature.set('data', {
                startTime: startPos.timestamp,
                endTime: endPos.timestamp,
                routeStartTime,
                routeEndTime,
            });

            lineFeatures.push(lineFeature);
        }
    });

    return lineFeatures;
}

function fetchAndMakeTailFeature(vehicleId: number, from?: number, to?: number) {
    return Ajax({
        url: 'ajax_vehicle_positiontail.json',
        data: {
            vehicleId,
            sinceUnixTimeMillis: from,
            untilUnixTimeMillis: to,
        },
    }).then(resp => ({
        resultTruncated: resp.resultTruncated,
        features: makeTailFeatures(resp.routes),
        routes: resp.routes,
    }));
}

export const VehiclesFeatureFactory = {
    makeFeature,

    makeTailFeatures,
    fetchAndMakeTailFeature,
    VehicleTrailDesktopMouseInteraction,

    styleFunction,
    styles,

    FEATURE_TYPE,
};
